const MAX_NATIVE_ZOOM = 22;
const MAX_OVERZOOM = 25;

function normalizeCoord(coord: google.maps.Point, zoom: number): { x: number; y: number } | null {
    const tileRange = 1 << zoom;
    const x = ((coord.x % tileRange) + tileRange) % tileRange;
    const y = coord.y;
    if (y < 0 || y >= tileRange) return null;
    return { x, y };
}

function getSatelliteTileUrl(x: number, y: number, z: number): string {
    const server = (x + y) % 4;
    return `https://mt${server}.google.com/vt/lyrs=s&x=${x}&y=${y}&z=${z}`;
}

function getHybridTileUrl(x: number, y: number, z: number): string {
    const server = (x + y) % 4;
    return `https://mt${server}.google.com/vt/lyrs=y&x=${x}&y=${y}&z=${z}`;
}

export class OverzoomSatelliteMapType implements google.maps.MapType {
    tileSize: google.maps.Size;
    maxZoom: number;
    minZoom: number;
    name: string;
    alt: string;
    projection: google.maps.Projection | null = null;
    radius: number = 6378137;
    private useHybrid: boolean;

    constructor(useHybrid: boolean = true) {
        this.tileSize = new google.maps.Size(256, 256);
        this.maxZoom = MAX_OVERZOOM;
        this.minZoom = 0;
        this.name = useHybrid ? "Hybrid (Super Zoom)" : "Satellite (Super Zoom)";
        this.alt = "Satellite imagery with extended zoom capability";
        this.useHybrid = useHybrid;
    }

    getTile(coord: google.maps.Point, zoom: number, ownerDocument: Document): HTMLElement {
        const normalized = normalizeCoord(coord, zoom);

        const div = ownerDocument.createElement("div");
        div.style.width = "256px";
        div.style.height = "256px";
        div.style.overflow = "hidden";

        if (!normalized) {
            div.style.backgroundColor = "#e0e0e0";
            return div;
        }

        const getTileUrl = this.useHybrid ? getHybridTileUrl : getSatelliteTileUrl;

        if (zoom <= MAX_NATIVE_ZOOM) {
            const url = getTileUrl(normalized.x, normalized.y, zoom);
            div.style.backgroundImage = `url("${url}")`;
            div.style.backgroundSize = "256px 256px";
            div.style.backgroundRepeat = "no-repeat";
            return div;
        }

        const dz = zoom - MAX_NATIVE_ZOOM;
        const scale = 1 << dz;

        const parentX = Math.floor(normalized.x / scale);
        const parentY = Math.floor(normalized.y / scale);

        const subX = normalized.x % scale;
        const subY = normalized.y % scale;

        const parentUrl = getTileUrl(parentX, parentY, MAX_NATIVE_ZOOM);

        div.style.backgroundImage = `url("${parentUrl}")`;
        div.style.backgroundSize = `${256 * scale}px ${256 * scale}px`;
        div.style.backgroundPosition = `${-subX * 256}px ${-subY * 256}px`;
        div.style.backgroundRepeat = "no-repeat";
        div.style.imageRendering = "auto";

        return div;
    }

    releaseTile(_tile: HTMLElement): void {
        // no need to implement this for now.
    }
}

export function enableOverzoom(map: google.maps.Map): void {
    const overzoomMapType = new OverzoomSatelliteMapType(true);
    map.mapTypes.set("overzoom_hybrid", overzoomMapType);
    map.setMapTypeId("overzoom_hybrid");
    map.setOptions({ maxZoom: MAX_OVERZOOM });
}

export function disableOverzoom(map: google.maps.Map): void {
    map.setMapTypeId("hybrid");
    map.setOptions({ maxZoom: MAX_NATIVE_ZOOM });

    // If currently zoomed beyond native max, zoom back
    const currentZoom = map.getZoom();
    if (currentZoom && currentZoom > MAX_NATIVE_ZOOM) {
        map.setZoom(MAX_NATIVE_ZOOM);
    }
}

export function toggleOverzoom(map: google.maps.Map, enabled: boolean): void {
    if (enabled) {
        enableOverzoom(map);
    } else {
        disableOverzoom(map);
    }
}

export { MAX_NATIVE_ZOOM, MAX_OVERZOOM };
